<template>
  <div class="d-flex align-center">
    <GAutocomplete
      v-model="selectedCountry"
      :items="countryOptions"
      item-value="code"
      item-text="emojiAndCode"
      persistent-hint
      outlined
      hide-selected
      clearable
      required
      :disabled="isDisabled"
      :menu-props="{ bottom: true, offsetY: true }"
      class="mr-2 custom-selection"
    />
    <v-text-field
      ref="inputField"
      v-model="formattedNumber"
      v-bind="$attrs"
      :label="label"
      :rules="wrappedRules"
      :required="required"
      :disabled="isDisabled"
      type="tel"
    >
      <slot
        v-for="(_, name) in $slots"
        :slot="name"
        :name="name"
      />
    </v-text-field>
  </div>
</template>

<script>
import {
  AsYouType, isPossiblePhoneNumber, isValidPhoneNumber, getCountries, getCountryCallingCode,
} from 'libphonenumber-js';
import { getDigitsFromString } from '@/helpers/formatting';
import { mapState } from 'vuex';
import metadata from 'libphonenumber-js/metadata.full.json';
import GAutocomplete from './GAutocomplete.vue';

export default {
  name: 'PhoneNumberInput',
  components: {
    GAutocomplete,
  },
  props: {
    value: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: 'Phone Number',
    },
    required: {
      type: Boolean,
      default: false,
    },
    useStandardRule: {
      type: Boolean,
      default: true,
    },
    useStrictRule: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    subscriptionCountry: {
      type: Object,
      default: null,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedCountry: this.subscriptionCountry
        ? this.subscriptionCountry.country_code2
        : 'CA',
    };
  },
  computed: {
    ...mapState('subscription', ['country']),

    countryOptions() {
      const getFlagEmoji = (countryCode) => countryCode.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

      return getCountries(metadata).map((code) => {
        const dialCode = `+${getCountryCallingCode(code, metadata)}`;
        return {
          code,
          dialCode,
          emoji: getFlagEmoji(code),
          emojiAndCode: `${getFlagEmoji(code)} ${dialCode}`,
        };
      });
    },

    formattedNumber: {
      get() {
        if (!this.value) return '';

        const ayt = new AsYouType(this.selectedCountry);
        ayt.input(this.value);
        return ayt.getNationalNumber() || '';
      },
      set(newValue) {
        if (!newValue) {
          this.$emit('input', '');
          return;
        }

        const dialCode = `+${getCountryCallingCode(this.selectedCountry, metadata)}`;
        const digits = getDigitsFromString(newValue);
        const formatted = `${dialCode} ${digits}`;
        this.$emit('input', formatted);
      },
    },

    rule() {
      return (v) => {
        if (!v) return !this.required || `${this.label} is required`;
        let nationalNumber = v;
        if (v.startsWith('1')) nationalNumber = v.slice(1);
        const valid = isPossiblePhoneNumber(nationalNumber, this.selectedCountry);
        return valid || `"${v}" is not a valid phone number`;
      };
    },

    strict() {
      return (v) => {
        if (!v) return !this.required || `${this.label} is required`;
        const fullValue = `${getCountryCallingCode(this.selectedCountry, metadata)}${getDigitsFromString(v)}`;
        return isValidPhoneNumber(fullValue, this.selectedCountry)
          || `"${v}" is not a valid phone number`;
      };
    },

    wrappedRules() {
      const out = [...this.rules];
      if (this.useStandardRule) out.push(this.rule);
      if (this.useStrictRule) out.push(this.strict);
      return out;
    },
  },

  mounted() {
    if (this.value && this.value.startsWith('+')) {
      const ayt = new AsYouType();
      ayt.input(this.value);
      this.selectedCountry = ayt.getCountry() || this.country.country_code2 || 'CA';
    } else {
      this.selectedCountry = this.country.country_code2 || 'CA';
    }
  },

  watch: {
    selectedCountry(newCountry) {
      if (!newCountry) {
        this.$emit('input', '');
        return;
      }
      if (this.formattedNumber) {
      // Trigger the setter logic to re-emit with new country code
        // eslint-disable-next-line no-self-assign
        this.formattedNumber = this.formattedNumber;
      }
      this.$nextTick(() => {
        this.$refs.inputField?.validate();
      });
    },

    subscriptionCountry: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.country_code2) {
          this.selectedCountry = newVal.country_code2;
        }
      },
    },
  },

  methods: {
    validate() {
      return this.$refs.inputField.valid;
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-selection {
  flex: 0 0 35%;
}
</style>
